import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import { TrendingUp, Target, Users, BarChart } from "lucide-react";
import { BarChart2 } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import { Monitor, Smartphone, Search, PenTool, Cloud } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function DigitalMarketing() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Search Engine Marketing",
      description: "Google Ads and Bing campaigns that drive qualified traffic and conversions.",
      icon: <Target className="h-8 w-8" />
    },
    {
      title: "Social Media Marketing",
      description: "Engaging social media campaigns across all major platforms.",
      icon: <Users className="h-8 w-8" />
    },
    {
      title: "Content Marketing",
      description: "Strategic content creation that educates, engages, and converts.",
      icon: <TrendingUp className="h-8 w-8" />
    },
    {
      title: "Analytics & Reporting",
      description: "Data-driven insights to optimize your marketing performance.",
      icon: <BarChart className="h-8 w-8" />
    }
  ];

  const platforms = [
    { name: "Google Ads", icon: "fab fa-google" },
    { name: "Facebook", icon: "fab fa-facebook" },
    { name: "Instagram", icon: "fab fa-instagram" },
    { name: "LinkedIn", icon: "fab fa-linkedin" },
    { name: "Twitter", icon: "fab fa-twitter" },
    { name: "TikTok", icon: "fab fa-tiktok" }
  ];

  return (
    <>
      <SEO 
        title="Digital Marketing Services UAE | Kecly"
        description="Full-service digital marketing in Dubai & Sharjah. SEO, PPC, social media & content marketing to grow your business online."
        keywords="digital marketing uae, seo services dubai, social media marketing sharjah"
      />

      <main>
       

        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-blue-50 to-purple-50 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated data visualization elements */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full bg-blue-400"
                  style={{
                    width: `${Math.random() * 10 + 5}px`,
                    height: `${Math.random() * 10 + 5}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: `pulse ${Math.random() * 5 + 3}s infinite alternate`
                  }}
                />
              ))}
            </div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
                  Digital Marketing
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-lg">
                  Data-driven strategies to grow your business online with measurable results and maximum ROI.
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-12">
                  {['Google Ads', 'SEO', 'Social Media', 'Email'].map((service) => (
                    <span key={service} className="px-4 py-2 bg-white rounded-full text-gray-700 border border-gray-300 shadow-sm">
                      {service}
                    </span>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Button 
                    onClick={openDrawer}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                  >
                    Get Started
                  </Button>
                  <Link href="/contact">
                    <Button 
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                    >
                      Learn More
                    </Button>
                  </Link>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Glass morphism card */}
                <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl p-8 shadow-2xl overflow-hidden">
                  <div className="absolute -right-20 -top-20 w-64 h-64 bg-purple-500/5 rounded-full filter blur-xl"></div>
                  <div className="flex justify-center mb-8">
                    <BarChart2 className="h-20 w-20 text-blue-400" />
                  </div>
                  
                  {/* Mini metrics dashboard */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      { label: 'Traffic', value: '+245%', color: 'bg-green-500' },
                      { label: 'Leads', value: '+189%', color: 'bg-blue-500' },
                      { label: 'ROI', value: '5.8x', color: 'bg-purple-500' },
                      { label: 'Engagement', value: '+312%', color: 'bg-pink-500' }
                    ].map((metric) => (
                      <div key={metric.label} className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className={`h-2 ${metric.color} rounded-full mb-2`}></div>
                        <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                        <div className="text-xs text-gray-600">{metric.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Social platform icons */}
                  <div className="flex justify-center space-x-4">
                    {['Google', 'Facebook', 'Instagram', 'LinkedIn'].map((platform) => (
                      <div key={platform} className="p-2 bg-gray-50 rounded-full">
                        <div className="h-6 w-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Floating platform badges */}
                <div className="absolute -bottom-5 -right-5 flex space-x-2">
                  {['Google', 'Meta', 'TikTok', 'LinkedIn'].map((platform) => (
                    <span key={platform} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-700 backdrop-blur-sm">
                      {platform}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Digital Marketing Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive marketing solutions to grow your business online
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-8 rounded-xl bg-white"
                >
                  <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mb-6 text-gray-900">
                    {service.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Marketing Strategy */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Our Marketing Approach</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Data-driven strategies that deliver measurable results
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Research & Analysis",
                  description: "Deep dive into your target audience, competitors, and market opportunities",
                  icon: "fas fa-search"
                },
                {
                  title: "Strategy Development",
                  description: "Create customized marketing strategies aligned with your business goals",
                  icon: "fas fa-lightbulb"
                },
                {
                  title: "Execution & Optimization",
                  description: "Implement campaigns and continuously optimize for better performance",
                  icon: "fas fa-rocket"
                }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${item.icon} text-xl text-gray-900`}></i>
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Marketing Platforms</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We leverage all major digital marketing platforms
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {platforms.map((platform, index) => (
                <motion.div
                  key={platform.name}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="glass-effect p-6 rounded-xl text-center bg-white"
                >
                  <i className={`${platform.icon} text-4xl text-gray-900 mb-4`}></i>
                  <p className="font-orbitron text-sm text-gray-600">{platform.name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Proven Results</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Our marketing campaigns deliver measurable business growth
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { metric: "300%", label: "Average ROI Increase" },
                { metric: "150%", label: "Traffic Growth" },
                { metric: "85%", label: "Lead Quality Improvement" },
                { metric: "200%", label: "Conversion Rate Boost" }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="font-orbitron font-bold text-4xl text-gray-900 mb-2">{stat.metric}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Digital Marketing" 
              services={[
                {
                  title: "SEO",
                  description: "Improve your search rankings and drive organic traffic to your website.",
                  icon: <Search className="h-8 w-8" />
                },
                {
                  title: "Web Design & Development",
                  description: "Custom websites and web applications tailored to your business needs.",
                  icon: <Monitor className="h-8 w-8" />
                },
                {
                  title: "Cloud Services",
                  description: "Cloud-based marketing analytics and campaign management tools.",
                  icon: <Cloud className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">
                Ready to Grow Your Business?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Let's create a digital marketing strategy that drives real results for your business
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openDrawer} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg">
                  Get Free Marketing Audit
                </Button>
                <Link href="/contact">
                  <Button className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg">
                    Schedule Strategy Call
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Analytics Dashboard */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Analytics Dashboard</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Get insights into your marketing performance with our analytics dashboard
              </p>
            </motion.div>

            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Floating gradient orbs */}
                <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-purple-500/10 rounded-full filter blur-3xl animate-pulse"></div>
                
                {/* Analytics dashboard mockup */}
                <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  {/* Browser chrome */}
                  <div className="bg-gray-100 p-2 flex items-center border-b border-gray-200">
                    <div className="flex space-x-2 mr-3">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 bg-white rounded text-xs px-3 py-1 text-gray-700 truncate">
                      analytics.yourbusiness.com
                    </div>
                  </div>
                  
                  {/* Dashboard content */}
                  <div className="p-4 bg-white">
                    {/* Stats row */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      {['Visitors', 'CTR', 'Conversions', 'ROI'].map((metric) => (
                        <div key={metric} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-xs text-gray-600 mb-1">{metric}</div>
                          <div className="h-6 w-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded mb-1"></div>
                          <div className="h-3 w-12 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Graph area */}
                    <div className="h-40 bg-gray-50 rounded-lg border border-gray-200 mb-4 relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-20 bg-gradient-to-t from-blue-500/10 to-transparent"></div>
                      </div>
                    </div>
                    
                    {/* Campaigns */}
                    <div className="grid grid-cols-2 gap-3">
                      {['Google Ads', 'Meta Ads', 'Email', 'SEO'].map((channel) => (
                        <div key={channel} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center mb-2">
                            <div className="h-6 w-6 bg-blue-100 rounded-lg mr-2 flex items-center justify-center">
                              <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                            </div>
                            <div className="text-sm text-gray-700">{channel}</div>
                          </div>
                          <div className="h-3 w-24 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute -bottom-5 -right-5 flex space-x-2">
                  {['Google', 'Meta', 'SEO', 'Analytics'].map((tech) => (
                    <span key={tech} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-700 backdrop-blur-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
